/**
 * Parses the final dify SSE `workflow_finished` payload — same paths as ChatAdvisor completion
 * (`modules/chatadvisor/src/lib/chatadvisor.tsx` → `completeStreamedMessage` after `workflow_finished`).
 */
export type ClaimScoreWorkflowSseChunk = {
  event?: string;
  data?: Record<string, unknown>;
  workflow_run_id?: string;
  task_id?: string;
  [key: string]: unknown;
};

function workflowDataAsRecord(value: unknown): Record<string, unknown> | undefined {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
}

function outputsFromWorkflowData(data: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!data) return undefined;
  const nested = workflowDataAsRecord(data.outputs);
  if (nested) return nested;
  return data;
}

/** Coerces Dify workflow `outputs.text` (often string, sometimes JSON number). */
export function coerceScoreTextFromOutputs(
  outputs: Record<string, unknown> | undefined,
  dataDirect: Record<string, unknown> | undefined,
): string | null {
  const rawUnknown = outputs?.text ?? dataDirect?.text;
  if (typeof rawUnknown === 'string' && rawUnknown.trim()) return rawUnknown.trim();
  if (typeof rawUnknown === 'number' && Number.isFinite(rawUnknown)) return String(rawUnknown);
  return null;
}

/** Unwrap `{ payload: { event, data } }` from gateways that wrap SSE JSON. */
export function normalizeClaimScoreSseEnvelope(parsed: unknown): ClaimScoreWorkflowSseChunk | null {
  if (parsed == null || typeof parsed !== 'object') return null;
  const top = parsed as Record<string, unknown>;
  let event = top.event;
  let dataUnknown: unknown = top.data;

  const pl = top.payload;
  if (pl != null && typeof pl === 'object' && !Array.isArray(pl)) {
    const p = pl as Record<string, unknown>;
    const innerEvent = p.event;
    if ((event == null || event === '') && typeof innerEvent === 'string') {
      event = innerEvent;
    }
    const innerData = p.data;
    if (innerData !== undefined) {
      const outer = workflowDataAsRecord(dataUnknown);
      const inner = workflowDataAsRecord(innerData);
      const outerHasOutputs = outer?.outputs != null;
      const innerHasOutputs = inner?.outputs != null;
      if (innerHasOutputs && !outerHasOutputs) {
        dataUnknown = innerData;
      }
    }
  }

  return {
    ...top,
    event: typeof event === 'string' ? event : undefined,
    data: workflowDataAsRecord(dataUnknown),
  };
}

/** Event name casing / whitespace tolerant. */
function isWorkflowFinishedEvent(event: string | undefined): boolean {
  if (typeof event !== 'string') return false;
  return event.trim().toLowerCase() === 'workflow_finished';
}

/**
 * Mirrors chatadvisor: `data?.outputs?.text || data?.text`
 */
export function getWorkflowFinishedScoreText(chunk: ClaimScoreWorkflowSseChunk): string | null {
  if (!isWorkflowFinishedEvent(chunk.event)) return null;
  const data = workflowDataAsRecord(chunk.data);
  const outputs = outputsFromWorkflowData(data);
  return coerceScoreTextFromOutputs(outputs, data);
}

function actionOutputFromOutputs(outputs: Record<string, unknown> | undefined): unknown {
  if (!outputs) return undefined;
  const typo = outputs.action_outpput;
  const alt = outputs.action_output;
  if (typo != null) return typo;
  return alt;
}

function appendNestedScoreExplanation(
  lines: string[],
  label: string,
  entry: Record<string, unknown>,
  field: string,
): void {
  const nested = workflowDataAsRecord(entry[field]);
  if (!nested) return;
  const scoreVal = nested.score;
  const expl = nested.explanation;
  if (typeof expl !== 'string' || !expl.trim()) return;
  const scorePart = typeof scoreVal === 'number' ? ` (${scoreVal})` : '';
  lines.push(`- **${label}**${scorePart}: ${expl.trim()}`);
}

function appendImageScoreLines(lines: string[], raw: unknown): void {
  if (!Array.isArray(raw)) return;
  raw.forEach((im, idx) => {
    const row = workflowDataAsRecord(im);
    if (!row) return;
    const s = typeof row.score === 'number' ? `${row.score}` : '—';
    const e = typeof row.explanation === 'string' ? row.explanation.trim() : '';
    if (e) lines.push(`- **Image score ${idx + 1}** (${s}): ${e}`);
  });
}

function appendHeadActionOutputLines(lines: string[], first: Record<string, unknown>): void {
  appendImageScoreLines(lines, first.image_scores);
  appendNestedScoreExplanation(lines, 'Dealer', first, 'dealer_score');
  appendNestedScoreExplanation(lines, 'Early / Late claim', first, 'early_late_claim');
  appendNestedScoreExplanation(lines, 'Valid policy', first, 'valid_policy');

  const claimScore = first.claim_score;
  if (typeof claimScore === 'number') lines.push(`- **Structured claim score**: ${claimScore}`);
  const claimAmt = first.claim_amount;
  if (typeof claimAmt === 'number') lines.push(`- **Claim amount**: ${claimAmt}`);
  const reqs = first.requests;
  if (Array.isArray(reqs) && reqs.length > 0) {
    lines.push(`- **Requests**: ${reqs.length} item(s) — see workflow logs for detail.`);
  }
}

function appendTailActionOutputLines(lines: string[], raw: unknown[]): void {
  for (let i = 1; i < raw.length; i += 1) {
    const row = workflowDataAsRecord(raw[i]);
    if (!row) continue;
    if (typeof row.duration_seconds === 'number') {
      lines.push(`- **Workflow duration**: ${row.duration_seconds.toFixed(3)}s`);
    }
    const sg = row.stopgates;
    if (Array.isArray(sg) && sg.length > 0) {
      lines.push(`- **Stopgates**: ${sg.length} active`);
    }
  }
}

/** Turns `outputs.action_outpput` into short markdown-ish bullets for the sidebar. */
export function formatClaimScoreActionOutputMarkdown(raw: unknown): string {
  if (!Array.isArray(raw) || raw.length === 0) return '';

  const lines: string[] = [];
  const first = workflowDataAsRecord(raw[0]);
  if (first) appendHeadActionOutputLines(lines, first);
  appendTailActionOutputLines(lines, raw);
  return lines.join('\n');
}

export type WorkflowFinishedParsed = Readonly<{
  scoreDisplay: number | null;
  scoreTextRaw: string;
  explanationMarkdown: string;
  outputsSnapshot: Record<string, unknown> | undefined;
}>;

/** Parse numeric score from workflow `outputs.text` (e.g. `"0.0"`, `"87.5"`). */
export function parseNumericScoreFromText(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const n = Number.parseFloat(trimmed.replace(/,/gu, ''));
  if (!Number.isFinite(n)) return null;
  return Math.round(Math.min(100, Math.max(0, n)));
}

/** Full extraction from `workflow_finished` SSE chunk (`data.outputs` mirrors chatadvisor). */
export function parseWorkflowFinishedForClaimScore(chunk: ClaimScoreWorkflowSseChunk): WorkflowFinishedParsed | null {
  if (!isWorkflowFinishedEvent(chunk.event)) return null;

  const data = workflowDataAsRecord(chunk.data);
  const outputs = outputsFromWorkflowData(data);
  const scoreTextRaw = getWorkflowFinishedScoreText(chunk);
  const actionSlice = formatClaimScoreActionOutputMarkdown(actionOutputFromOutputs(outputs));

  let explanationMarkdown = actionSlice.trim();
  if (!explanationMarkdown && outputs) {
    const keys = ['text', 'action_outpput', 'action_output','dummy_claim_id'];
    const rest = { ...outputs };
    keys.forEach((k) => {
      delete rest[k];
    });
    const leftover = stableStringForFallback(rest);
    if (leftover) explanationMarkdown = leftover;
  }

  const scoreDisplay = scoreTextRaw === null ? null : parseNumericScoreFromText(scoreTextRaw);

  return {
    scoreDisplay,
    scoreTextRaw: scoreTextRaw ?? '',
    explanationMarkdown,
    outputsSnapshot: outputs,
  };
}

function stableStringForFallback(obj: Record<string, unknown>): string {
  if (Object.keys(obj).length === 0) return '';
  try {
    return '```json\n' + JSON.stringify(obj, null, 2).slice(0, 6000) + '\n```';
  } catch {
    return '';
  }
}
